import React, { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import EKYCCardsUnitsRC from "./EKYCCardsUnitsRC";

const EKYCCardsUnitsFPS = ({
  distMonth,
  distYear,
  distMonthName,
  distCode,
  distName,
  afsoCode,
  afsoName,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schemeData, setSchemeData] = useState([]);
  const [fpsData, setFPSData] = useState([]);
  const [selectedFPS, setSelectedFPS] = useState(null);

  const fields = [
    "total_rc",
    "total_units",
    "fully_ekyc_completed_rc",
    "fully_ekyc_done_units",
    "partial_ekyc_done_rc",
    "partial_ekyc_done_units",
    "partial_ekyc_not_done_units",
    "ekyc_not_done_rc",
    "ekyc_not_done_units",
  ];

  const formatHeader = (field) => {
    switch (field) {
      case "total_rc":
        return "Total\nRC";
      case "total_units":
        return "Total\nUnits";
      case "fully_ekyc_completed_rc":
        return "Fully\ne-KYC\nCompleted\nRC";
      case "fully_ekyc_done_units":
        return "Fully\ne-KYC\nDone\nUnits";
      case "partial_ekyc_done_rc":
        return "Partial\ne-KYC\nDone\nRC";
      case "partial_ekyc_done_units":
        return "Partial\ne-KYC\nDone\nUnits";
      case "partial_ekyc_not_done_units":
        return "Partial\ne-KYC\nNot\nDone\nUnits";
      case "ekyc_not_done_rc":
        return "e-KYC\nNot\nDone\nRC";
      case "ekyc_not_done_units":
        return "e-KYC\nNot\nDone\nUnits";
      default:
        return field.replace(/_/g, "\n");
    }
  };

  const getSchemeTotals = (schemeId, field) =>
    fpsData.reduce(
      (sum, item) => sum + Number(item[`${field}_${schemeId}`] || 0),
      0
    );

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [schemeRes, fpsRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_URL}/api/ekyc-info/ekyc-card-units-scheme?distMonth=${distMonth}&distYear=${distYear}`
        ),
        fetch(
          `${process.env.REACT_APP_URL}/api/ekyc-info/ekyc-fps-card-units?distMonth=${distMonth}&distYear=${distYear}&distCode=${distCode}&afsoCode=${afsoCode}`
        ),
      ]);

      if (!schemeRes.ok) throw new Error("Failed to fetch Scheme Details");
      if (!fpsRes.ok) throw new Error("Failed to fetch FPS Details");

      setSchemeData(await schemeRes.json());
      setFPSData(await fpsRes.json());
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [distMonth, distYear, distCode, afsoCode]);

  const handleFPSClick = (fpsId, data_date) => {
    setSelectedFPS({ fpsId, ekycGeneratedDate: data_date });
  };

  const handleDownloadExcel = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("EKYC FPS Report");

  const totalColumns = 2 + schemeData.length * fields.length;

  // === Title Row ===
  const title = `EKYC Card Unit Status in District: ${distName}, Mandal: ${afsoName}, Month: ${distMonthName} ${distYear}, Date: ${
    fpsData[0]?.data_date
      ? new Date(fpsData[0].data_date).toLocaleDateString("en-GB")
      : ""
  }`;
  const titleRow = worksheet.addRow([title]);
  worksheet.mergeCells(1, 1, 1, totalColumns);
  titleRow.eachCell((cell) => {
    cell.font = { bold: true, size: 14 };
    cell.alignment = { horizontal: "center", vertical: "middle" };
  });

  // === Header Row 1: Schemes ===
  const headerRow1 = ["", ""];
  schemeData.forEach((scheme) => {
    headerRow1.push(scheme.schemename);
    for (let i = 1; i < fields.length; i++) headerRow1.push("");
  });
  const row1 = worksheet.addRow(headerRow1);

  // Merge scheme header cells
  let colStart = 3;
  schemeData.forEach(() => {
    worksheet.mergeCells(2, colStart, 2, colStart + fields.length - 1);
    colStart += fields.length;
  });

  // === Header Row 2: Fields ===
  const headerRow2 = ["S.No", "FPS Id"];
  schemeData.forEach(() => {
    fields.forEach((f) => headerRow2.push(formatHeader(f)));
  });
  worksheet.addRow(headerRow2);
  worksheet.getRow(3).height = 65;

  // === Style headers ===
  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber <= 3) {
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });
    }
  });

  // === Data Rows ===
  fpsData.forEach((item, idx) => {
    const rowData = [
      idx + 1,
      item.fps_id,
      ...schemeData.flatMap((scheme) =>
        fields.map((f) => item[`${f}_${scheme.schemeid}`] || 0)
      ),
    ];
    worksheet.addRow(rowData);
  });

  // === Total Row ===
  const totalRowData = [
    "",
    "TOTAL",
    ...schemeData.flatMap((scheme) => fields.map((f) => getSchemeTotals(scheme.schemeid, f))),
  ];
  const totalRow = worksheet.addRow(totalRowData);
  totalRow.font = { bold: true };

  // === Column widths & freeze headers ===
  worksheet.columns.forEach((col) => (col.width = 18));
  worksheet.views = [{ state: "frozen", ySplit: 3 }];

  // === Export Excel ===
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `EKYC_FPS_Report_${distMonthName}_${distYear}.xlsx`);
};

  

  

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <div style={{ textAlign: "center", color: "red" }}>{error}</div>;
  }

  if (!schemeData.length || !fpsData.length) {
    return <div style={{ textAlign: "center" }}>No data found</div>;
  }

  return (
    <div style={{ margin: "0 auto 120px auto", padding: "0 20px" }}>
      <div
        style={{
          overflowY: "auto",
          overflowX: "auto",
          width: "100%",
          border: "1px solid #ccc",
        }}
      >
        <table
          width="100%"
          border="1"
          cellPadding="5"
          cellSpacing="0"
          style={{ minWidth: "100%", width: "max-content" }}
        >
          <thead style={{ position: "sticky", top: 0 }}>
            <tr className="tableheader">
              <th colSpan={2 + schemeData.length * fields.length}>
                EKYC Card Unit Status in District: {distName}, Mandal:{" "}
                {afsoName}, Month: {distMonthName} {distYear}, Date:{" "}
                {fpsData[0]?.data_date
                  ? new Date(fpsData[0].data_date).toLocaleDateString("en-GB")
                  : ""}
              </th>
            </tr>

            <tr className="tableheader">
              <th rowSpan={2}>S.No</th>
              <th rowSpan={2}>FPS Id</th>
              {schemeData.map((scheme) => (
                <th key={scheme.schemeid} colSpan={fields.length}>
                  {scheme.schemename}
                </th>
              ))}
            </tr>

            <tr className="tableheader">
              {schemeData.map((scheme) =>
                fields.map((f) => (
                  <th
                    key={`${scheme.schemeid}-${f}`}
                    style={{ textAlign: "left", whiteSpace: "pre-line" }}
                  >
                    {formatHeader(f)}
                  </th>
                ))
              )}
            </tr>
          </thead>

          <tbody>
            {fpsData.map((item, index) => (
              <tr key={index}>
                <td style={{ textAlign: "center" }}>{index + 1}</td>
                <td style={{ textAlign: "left" }}>
                  <a
                    style={{ cursor: "pointer", color: "blue" }}
                    onClick={() => handleFPSClick(item.fps_id, item.data_date)}
                  >
                    {item.fps_id}
                  </a>
                </td>

                {schemeData.map((scheme) =>
                  fields.map((f) => (
                    <td
                      key={`${scheme.schemeid}-${f}`}
                      style={{ textAlign: "right" }}
                    >
                      {item[`${f}_${scheme.schemeid}`]}
                    </td>
                  ))
                )}
              </tr>
            ))}
          </tbody>

          <tfoot>
            <tr style={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
              <td colSpan={2} style={{ textAlign: "center" }}>
                TOTAL
              </td>
              {schemeData.map((scheme) =>
                fields.map((f) => (
                  <td
                    key={`${scheme.schemeid}-${f}`}
                    style={{ textAlign: "right" }}
                  >
                    {getSchemeTotals(scheme.schemeid, f)}
                  </td>
                ))
              )}
            </tr>
          </tfoot>
        </table>

        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <button
            className="button button2"
            onClick={handleDownloadExcel} 
          >
            Download Excel
          </button>
        </div>
      </div>

      {selectedFPS && (
        <EKYCCardsUnitsRC
          key={selectedFPS.fpsId}
          distMonth={distMonth}
          distYear={distYear}
          distMonthName={distMonthName}
          distName={distName}
          afsoName={afsoName}
          fpsId={selectedFPS.fpsId}
          ekycGeneratedDate={selectedFPS.ekycGeneratedDate}
        />
      )}
      <br />
      <br />
      <br />
    </div>
  );
};

export default EKYCCardsUnitsFPS;
